import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  getRoom, getRoomSignalingUrl, getIceServers,
} from 'Api/roomsApi';
import projectorLogo from 'Assets/Login Logo.png';
import LightRays from 'Components/reactbits/LightRays';
import { InterviewCall } from './InterviewCall';
import styles from './InterviewJoin.module.css';

// Guest entry for an interview-record session.
//
// URL: /interview/join/:roomId — anyone with the link gets in. No auth.
//
// State machine:
//   1. checking      — GET /rooms/{id}; validates the link.
//   2. ready-to-join — room exists + open; show camera preview + Join CTA.
//   3. joining       — Join clicked; getUserMedia + WS open as 'guest'.
//   4. waiting       — WS open, host either present or expected shortly.
//   5. error         — link bad / room closed / room full / permissions.
//
// The page intentionally does NOT auto-acquire camera on mount — many
// guests will land here on mobile during a busy moment, and a surprise
// "browser wants to use your camera" banner is hostile UX. They tap Join
// to give explicit consent.
//
// Layout: a fixed, non-scrolling shell (top status bar + one stage card that
// splits into camera preview / join panel). Every control the guest needs —
// mic, camera, name, Join — is on screen at once at every breakpoint; the
// preview is the only elastic element, so the buttons can't be pushed off.
// See InterviewJoin.module.css for the design-system notes.

// ── Icons (inline, stroke-based — no icon dependency) ──────────────────────
const IconMic = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);

const IconMicOff = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
    <line x1="12" y1="19" x2="12" y2="23" />
  </svg>
);

const IconCam = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const IconCamOff = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const IconUser = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const IconArrowRight = ({ className }) => (
  <svg className={className} width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const IconShield = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconAlert = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="13" />
    <line x1="12" y1="16.5" x2="12" y2="16.5" />
  </svg>
);

const IconCheck = ({ size = 30, strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconCheckTiny = () => <IconCheck size={11} strokeWidth={3} />;

const IconWave = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 16.5V8a1.5 1.5 0 0 0-3 0v-1a1.5 1.5 0 0 0-3 0v1a1.5 1.5 0 0 0-3 0v6" />
    <path d="M7 14l-1.2-1.2a1.6 1.6 0 0 0-2.3 2.3l3 3A6 6 0 0 0 10.8 20H13a5 5 0 0 0 5-5V9a1.5 1.5 0 0 0-2 -1.4" />
  </svg>
);

const IconSparkle = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.7L18.6 9.6l-4.7 1.9L12 16.2l-1.9-4.7L5.4 9.6l4.7-1.9L12 3z" />
  </svg>
);

// Initials for the camera-off disc — "Ada Lovelace" → "AL".
const initialsOf = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const InterviewJoin = () => {
  const { roomId } = useParams();

  const [phase, setPhase] = useState('checking');
  const [error, setError] = useState('');
  const [hostConnected, setHostConnected] = useState(false);
  const [ws, setWs] = useState(null);
  const [iceServers, setIceServers] = useState([]);
  const [stream, setStream] = useState(null);
  const [inCall, setInCall] = useState(false);
  // Pre-join lobby choices (Zoom-style): the guest can decide to join with
  // mic muted and/or camera off before granting permission. These are
  // INTENT only until Join acquires the stream — after that the toggles act
  // on the real tracks, and InterviewCall picks the state up from them.
  const [joinMuted, setJoinMuted] = useState(false);
  const [joinCameraOff, setJoinCameraOff] = useState(false);
  // Optional display name — shown to the host (and on the guest's own
  // camera-off avatar) in place of the generic "Guest".
  const [guestName, setGuestName] = useState('');
  // Bumped on each peer-joined(host) so InterviewCall remounts with a
  // fresh PeerConnection if the host reconnects. See InterviewHost for
  // the symmetric reasoning.
  const [negotiationKey, setNegotiationKey] = useState(0);
  // End-of-session state for the guest. Set when:
  //   • 'finished'    — host sent {type:'bye'} (intentional Stop/Leave)
  //   • 'disconnected'— host's WS dropped without a bye (tab close,
  //                     network loss). Two different messages: the
  //                     finished one is celebratory, disconnected is
  //                     apologetic. Once set, never overwritten — a
  //                     trailing peer-left after a bye is ignored.
  const [sessionEnded, setSessionEnded] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const wsRef = useRef(null);

  const teardown = useCallback(() => {
    try { if (wsRef.current) wsRef.current.close(); } catch (e) { /* noop */ }
    wsRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (e) { /* noop */ }
      });
      streamRef.current = null;
    }
  }, []);

  // Validate the room exists + is joinable before showing the Join CTA.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!roomId) {
        setError('This link looks broken. Ask for a new one.');
        setPhase('error');
        return;
      }
      try {
        const r = await getRoom(roomId);
        if (cancelled) return;
        if (r.status !== 'open' && r.status !== 'active') {
          setError('This meeting has ended. Ask for a new link.');
          setPhase('error');
          return;
        }
        if (r.is_full && !r.host_connected) {
          // Guest slot taken by someone else.
          setError('Someone has already joined this meeting from another device.');
          setPhase('error');
          return;
        }
        setHostConnected(!!r.host_connected);
        setPhase('ready-to-join');
      } catch (e) {
        if (!cancelled) {
          setError('We couldn\'t find that meeting. Ask for a fresh link.');
          setPhase('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [roomId]);

  // Teardown on unmount (back button, tab close, etc.).
  useEffect(() => () => teardown(), [teardown]);

  // Lobby toggles. Before Join they're pure intent; once the stream exists
  // (phase 'waiting') they flip the live track so the preview and what the
  // host will receive stay in sync with what the buttons say.
  const toggleMic = () => {
    setJoinMuted((prev) => {
      const next = !prev;
      const track = streamRef.current && streamRef.current.getAudioTracks()[0];
      if (track) track.enabled = !next;
      return next;
    });
  };

  const toggleCamera = () => {
    setJoinCameraOff((prev) => {
      const next = !prev;
      const track = streamRef.current && streamRef.current.getVideoTracks()[0];
      if (track) track.enabled = !next;
      return next;
    });
  };

  const handleJoin = async () => {
    if (!guestName.trim()) return;
    setPhase('joining');
    setError('');
    // 1) Permissions + ICE servers in parallel. ICE is needed by
    //    InterviewCall when WebRTC starts, so we fetch up front.
    let acquired;
    let ice;
    try {
      [acquired, ice] = await Promise.all([
        navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
          audio: { echoCancellation: true, noiseSuppression: true },
        }),
        getIceServers().catch(() => null),
      ]);
    } catch (e) {
      setError('We need access to your camera and microphone to join.');
      setPhase('error');
      return;
    }
    // Apply the lobby choices to the freshly-acquired tracks before the
    // call starts, so the guest joins exactly how they picked. InterviewCall
    // reads these track states for its initial mute / camera-off UI.
    const aTrack = acquired.getAudioTracks()[0];
    if (aTrack) aTrack.enabled = !joinMuted;
    const vTrack = acquired.getVideoTracks()[0];
    if (vTrack) vTrack.enabled = !joinCameraOff;

    streamRef.current = acquired;
    setStream(acquired);
    setIceServers((ice && ice.iceServers) || []);
    if (videoRef.current) {
      videoRef.current.srcObject = acquired;
    }

    // 2) Signaling socket.
    let sock;
    try {
      sock = new WebSocket(getRoomSignalingUrl(roomId, 'guest'));
    } catch (e) {
      setError('Could not connect to the meeting. Please try again.');
      setPhase('error');
      return;
    }
    wsRef.current = sock;
    sock.onopen = () => {
      setWs(sock);
      setPhase('waiting');
    };
    sock.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg && msg.type === 'peer-joined' && msg.role === 'host') {
          setHostConnected(true);
          // Fresh mount on host re-join, mirrors the host-side logic.
          setNegotiationKey((k) => k + 1);
        }
        if (msg && msg.type === 'peer-left' && msg.role === 'host') {
          setHostConnected(false);
          // peer-left after a bye is normal teardown — don't overwrite
          // the friendlier 'finished' state with 'disconnected'.
          setSessionEnded((prev) => prev || 'disconnected');
        }
        if (msg && msg.type === 'bye' && msg.from === 'host') {
          setSessionEnded((prev) => prev || 'finished');
        }
      } catch (e) { /* ignore unparseable messages */ }
    };
    sock.onclose = (ev) => {
      wsRef.current = null;
      setWs(null);
      // 4001 is our consumer's "slot taken / room invalid" close code.
      if (ev && ev.code === 4001) {
        setError('Someone else just joined this meeting.');
        setPhase('error');
      }
    };
  };

  // Auto-transition into the call view once we know the host is here +
  // the WS is open. The host page does the same on its side; both peers
  // arrive at InterviewCall, which negotiates the WebRTC handshake.
  useEffect(() => {
    if (!inCall && hostConnected && ws && stream && phase === 'waiting') {
      setInCall(true);
    }
  }, [inCall, hostConnected, ws, stream, phase]);

  // When the session ends (host finished or disconnected), tear down
  // the guest's call resources so the camera + mic light go out and
  // the dead PC stops chewing on ICE retries. The render below replaces
  // the call view with a full-screen end-state message.
  useEffect(() => {
    if (sessionEnded) teardown();
  }, [sessionEnded, teardown]);

  const handleLeaveCall = () => {
    teardown();
    // Guest has no onboarding context — just send them back to the home
    // page (they accessed this via a shared link, so leaving = exit app).
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  const rays = (
    <div className={styles.raysLayer} aria-hidden="true">
      <LightRays
        raysOrigin="top-center"
        raysColor="#ffffff"
        raysSpeed={0.9}
        lightSpread={0.85}
        rayLength={1.25}
        fadeDistance={1.1}
        saturation={1}
        followMouse
        mouseInfluence={0.08}
        noiseAmount={0.08}
        distortion={0.04}
      />
    </div>
  );

  // ── Session-ended view — takes priority over every other state so
  // a host that finishes mid-call doesn't fall back to the join page
  // or leave the call view stuck. Full-viewport, single CTA. ──
  if (sessionEnded) {
    const isFinished = sessionEnded === 'finished';
    return (
      <div className={styles.page}>
        {rays}
        <div className={styles.topBar}>
          <img src={projectorLogo} alt="Projector" className={styles.logo} />
        </div>

        <div className={styles.endShell}>
          <div className={styles.endCard}>
            <div className={`${styles.endHalo} ${isFinished ? '' : styles.endHaloNeutral}`}>
              {isFinished ? <IconCheck /> : <IconWave />}
            </div>
            <h1 className={styles.endTitle}>
              {isFinished ? 'Session complete' : 'The host left'}
            </h1>
            <p className={styles.endText}>
              {isFinished
                ? 'Thanks for being part of the conversation. Your host has the recording — they\'ll take it from here.'
                : 'Looks like the host\'s connection dropped. You can close this tab; they\'ll send a fresh link if they want to pick it up again.'}
            </p>
            <div className={styles.endCta}>
              <button type="button" onClick={handleLeaveCall} className={styles.primary}>
                Done
                <IconArrowRight className={styles.primaryIcon} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── In-call view — full-viewport, no card wrapper. The `key` prop
  // remounts InterviewCall on host reconnect, see negotiationKey. ──
  if (inCall) {
    return (
      <InterviewCall
        key={`call-${negotiationKey}`}
        peerRole="guest"
        localName={guestName.trim()}
        localStream={stream}
        ws={ws}
        iceServers={iceServers}
        onLeave={handleLeaveCall}
      />
    );
  }

  const isWaiting = phase === 'waiting';
  const isError = phase === 'error';
  const canJoin = phase === 'ready-to-join' && !!guestName.trim();
  // The device dock is only meaningful while the guest still controls their
  // own preview — hide it on the error state, where there's nothing to toggle.
  const showDock = !isError;

  const statusLabel = (() => {
    if (isError) return 'Link problem';
    if (phase === 'checking') return 'Checking link';
    if (phase === 'joining') return 'Connecting';
    if (isWaiting) return hostConnected ? 'Host is here' : 'Waiting for host';
    return 'Ready to join';
  })();
  const statusDotClass = (() => {
    if (isError) return styles.dotError;
    if (isWaiting && hostConnected) return styles.dotLive;
    if (isWaiting || phase === 'joining' || phase === 'checking') return styles.dotWait;
    return styles.dotLive;
  })();

  return (
    <div className={styles.page}>
      {rays}

      <div className={styles.topBar}>
        <img src={projectorLogo} alt="Projector" className={styles.logo} />
        <div className={styles.statusPill} role="status" aria-live="polite">
          <span className={`${styles.dot} ${statusDotClass}`} aria-hidden="true" />
          {statusLabel}
        </div>
      </div>

      <div className={styles.shell}>
        <div className={styles.stage}>

          {/* ── Camera preview: the stage's elastic half. Overlays cover it
                per phase; the device dock floats over its bottom edge so the
                mic / camera controls never take a row of their own. ── */}
          <div className={styles.preview}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={`${styles.video} ${!stream || joinCameraOff ? styles.videoHidden : ''}`}
            />

            {isWaiting && !joinCameraOff && guestName.trim() && (
              <div className={styles.previewTag}>
                <IconUser size={13} />
                {guestName.trim()}
                {' '}
                (you)
              </div>
            )}

            {phase === 'checking' && (
              <div className={styles.overlay}>
                <div className={styles.spinner} aria-hidden="true" />
                <p className={styles.overlayTitle}>Checking your invite…</p>
                <p className={styles.overlayText}>One moment while we open the room.</p>
              </div>
            )}

            {phase === 'ready-to-join' && (
              <div className={styles.overlay}>
                <div className={styles.overlayHalo}>
                  <IconCam />
                </div>
                <p className={styles.overlayTitle}>Your camera turns on when you join</p>
                {/* Optional blurb: the panel below says the same thing, so the
                    phone layout hides it to keep the short preview uncluttered. */}
                <p className={`${styles.overlayText} ${styles.overlayTextOptional}`}>
                  Pick how you&apos;d like to arrive below — you can change it any time
                  once you&apos;re in.
                </p>
              </div>
            )}

            {phase === 'joining' && (
              <div className={styles.overlay}>
                <div className={styles.spinner} aria-hidden="true" />
                <p className={styles.overlayTitle}>Asking for camera + mic…</p>
                <p className={styles.overlayText}>Allow access in your browser prompt to continue.</p>
              </div>
            )}

            {isWaiting && joinCameraOff && (
              <div className={styles.overlay}>
                <div className={styles.avatar} aria-hidden="true">{initialsOf(guestName)}</div>
                <p className={styles.overlayText}>
                  Your camera is off. The host will see your initials.
                </p>
              </div>
            )}

            {isError && (
              <div className={styles.overlay}>
                <div className={`${styles.overlayHalo} ${styles.overlayHaloError}`}>
                  <IconAlert />
                </div>
                <p className={styles.overlayTitle}>We couldn&apos;t get you in</p>
                <p className={styles.overlayText}>{error}</p>
              </div>
            )}

            {showDock && (
              <div className={styles.dockRow}>
                <div className={styles.dock}>
                  <button
                    type="button"
                    onClick={toggleMic}
                    aria-pressed={joinMuted}
                    aria-label={joinMuted ? 'Turn microphone on' : 'Turn microphone off'}
                    title={joinMuted ? 'Microphone off' : 'Microphone on'}
                    className={`${styles.dockBtn} ${joinMuted ? styles.dockBtnOff : ''}`}
                  >
                    {joinMuted ? <IconMicOff /> : <IconMic />}
                    <span className={styles.dockLabel}>{joinMuted ? 'Mic off' : 'Mic on'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={toggleCamera}
                    aria-pressed={joinCameraOff}
                    aria-label={joinCameraOff ? 'Turn camera on' : 'Turn camera off'}
                    title={joinCameraOff ? 'Camera off' : 'Camera on'}
                    className={`${styles.dockBtn} ${joinCameraOff ? styles.dockBtnOff : ''}`}
                  >
                    {joinCameraOff ? <IconCamOff /> : <IconCam />}
                    <span className={styles.dockLabel}>{joinCameraOff ? 'Camera off' : 'Camera on'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ── Join panel: the fixed-height half. Whatever the phase, the
                actionable control sits here so it's always in view. ── */}
          <div className={styles.panel}>
            <div className={styles.panelStack}>
              <div className={styles.head}>
                <span className={styles.eyebrow}>
                  <span className={styles.eyebrowDot} aria-hidden="true" />
                  Live recording session
                </span>

                <h1 className={styles.title}>
                  {isWaiting
                    ? 'You\'re in the room'
                    : isError
                      ? 'This invite needs a refresh'
                      : 'You\'re invited'}
                </h1>

                <p className={styles.subtitle}>
                  {isWaiting
                    ? 'Sit tight — your host starts the recording when they\'re ready. Keep this tab open.'
                    : isError
                      ? 'Nothing you did wrong. Try again, or ask your host to resend the link.'
                      : 'Join the conversation. Your host records the whole thing right from their browser — no app, no downloads.'}
                </p>
              </div>

              {phase === 'ready-to-join' && (
                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor="guest-name">Your name</label>
                  <div className={styles.inputWrap}>
                    <span className={styles.inputIcon} aria-hidden="true"><IconUser /></span>
                    <input
                      id="guest-name"
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value.slice(0, 40))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && guestName.trim()) handleJoin();
                      }}
                      placeholder="e.g. Alex Morgan"
                      aria-label="Your name"
                      autoComplete="name"
                      required
                      className={styles.input}
                    />
                  </div>
                </div>
              )}

              {(phase === 'ready-to-join' || phase === 'joining') && (
                <button
                  type="button"
                  onClick={handleJoin}
                  disabled={!canJoin}
                  className={styles.primary}
                >
                  {phase === 'joining' ? 'Joining…' : 'Join meeting'}
                  {phase !== 'joining' && <IconArrowRight className={styles.primaryIcon} />}
                </button>
              )}

              {phase === 'checking' && (
                <div className={styles.waitBox}>
                  <div className={styles.waitDots} aria-hidden="true">
                    <span className={styles.waitDot} />
                    <span className={styles.waitDot} />
                    <span className={styles.waitDot} />
                  </div>
                  <div>
                    <p className={styles.waitBoxTitle}>Opening the room</p>
                    <p className={styles.waitBoxText}>We&apos;re verifying your invite link.</p>
                  </div>
                </div>
              )}

              {isWaiting && (
                <div className={styles.waitBox}>
                  <div className={styles.waitDots} aria-hidden="true">
                    <span className={styles.waitDot} />
                    <span className={styles.waitDot} />
                    <span className={styles.waitDot} />
                  </div>
                  <div>
                    <p className={styles.waitBoxTitle}>
                      {hostConnected ? 'Host is here' : 'Waiting for your host'}
                    </p>
                    <p className={styles.waitBoxText}>
                      {hostConnected
                        ? 'They\'ll start the recording in a moment.'
                        : 'They\'ll appear as soon as they open the link.'}
                    </p>
                  </div>
                </div>
              )}

              {isWaiting && (
                <button
                  type="button"
                  onClick={handleLeaveCall}
                  className={styles.secondary}
                >
                  Leave lobby
                </button>
              )}

              {isError && (
                <>
                  <div className={styles.errorBox}>
                    <IconAlert />
                    <span>{error}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className={styles.secondary}
                  >
                    Try again
                  </button>
                </>
              )}

              {phase === 'ready-to-join' && (
                <ul className={styles.steps}>
                  <li className={styles.step}>
                    <span className={styles.stepIcon} aria-hidden="true"><IconCheckTiny /></span>
                    Nothing to install — this runs in your browser.
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepIcon} aria-hidden="true"><IconCheckTiny /></span>
                    Your host controls when the recording starts and stops.
                  </li>
                  <li className={styles.step}>
                    <span className={styles.stepIcon} aria-hidden="true"><IconSparkle /></span>
                    Find a quiet spot with light on your face — it makes a difference.
                  </li>
                </ul>
              )}

              {!isError && (
                <p className={styles.note}>
                  <IconShield />
                  {isWaiting ? (
                    <span>
                      You control your mic and camera from the buttons on your preview.
                      The recording lives with your host.
                    </span>
                  ) : (
                    <span>
                      Your camera and mic stay off until you tap
                      {' '}
                      <strong>Join meeting</strong>
                      . The recording lives with your host.
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InterviewJoin;
